import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  IconButton,
  Avatar,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  SwitchProps,
  Tooltip,
} from '@mui/material';
import {
  LocalShippingOutlined,
  ExpandMore,
  ExpandLess,
  SaveOutlined,
  InventoryOutlined,
  PeopleOutlined,
  PaymentOutlined,
  DashboardOutlined,
  SettingsOutlined,
  ReportOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';

// Types
interface Permission {
  view: boolean;
  edit: boolean;
}

interface SubTask {
  id: string;
  name: string;
  permissions: Permission;
}

interface Page {
  id: string;
  name: string;
  icon: string;
  permissions: Permission;
  children: SubTask[];
}

interface IndeterminateSwitchProps extends SwitchProps {
  indeterminate?: boolean;
}

// Styled Components
const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const Info = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: 1,
});

const Col = styled(Box)({ width: 120, textAlign: 'center' });

const StyledSwitch = styled((props: IndeterminateSwitchProps) => {
  const { indeterminate, ...rest } = props;

  return (
    <Switch
      {...rest}
      checked={indeterminate ? false : rest.checked}
      sx={{
        ...(indeterminate && {
          '& .MuiSwitch-switchBase': {
            color: theme => theme.palette.secondary.main,
          },
          '& .MuiSwitch-track': {
            backgroundColor: theme => alpha(theme.palette.secondary.main, 0.5),
            opacity: 1,
          },
        }),
      }}
    />
  );
})(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.primary.light,
    opacity: 1,
  },
  '& .MuiSwitch-track': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.3),
  },
  '& .MuiSwitch-switchBase': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const AvatarBox = styled(Avatar)(({ theme }) => ({
  background: alpha(theme.palette.secondary.main, 0.1),
  color: theme.palette.primary.main,
}));

const SaveButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#fff',
  padding: '12px 32px',
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '16px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
  },
  '&:disabled': {
    background: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.contrastText,
    opacity: 0.5,
  },
  transition: 'all 0.3s ease',
}));

// Icon mapping - memoized at module scope
const ICON_MAP: Record<string, React.ReactElement> = {
  dashboard: <DashboardOutlined />,
  shipment: <LocalShippingOutlined />,
  inventory: <InventoryOutlined />,
  users: <PeopleOutlined />,
  payments: <PaymentOutlined />,
  reports: <ReportOutlined />,
  settings: <SettingsOutlined />,
};

const getIcon = (iconName: string): React.ReactElement => {
  return ICON_MAP[iconName] || ICON_MAP.dashboard;
};

// Mock Data - memoized at module scope
const MOCK_DATA: Page[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: 'dashboard',
    permissions: { view: true, edit: false },
    children: [
      { id: 'analytics', name: 'Analytics', permissions: { view: true, edit: false } },
      { id: 'reports', name: 'Reports', permissions: { view: true, edit: false } },
      { id: 'statistics', name: 'Statistics', permissions: { view: false, edit: false } },
    ],
  },
  {
    id: 'shipment',
    name: 'Shipment Management',
    icon: 'shipment',
    permissions: { view: true, edit: false },
    children: [
      { id: 'list', name: 'Shipment List', permissions: { view: true, edit: false } },
      { id: 'details', name: 'Shipment Details', permissions: { view: false, edit: false } },
      { id: 'tracking', name: 'Real-time Tracking', permissions: { view: true, edit: false } },
      { id: 'schedule', name: 'Schedule', permissions: { view: false, edit: false } },
    ],
  },
  {
    id: 'inventory',
    name: 'Inventory',
    icon: 'inventory',
    permissions: { view: true, edit: false },
    children: [
      { id: 'products', name: 'Products', permissions: { view: true, edit: false } },
      { id: 'stock', name: 'Stock Levels', permissions: { view: true, edit: false } },
      { id: 'warehouse', name: 'Warehouse', permissions: { view: false, edit: false } },
      { id: 'categories', name: 'Categories', permissions: { view: false, edit: false } },
    ],
  },
  {
    id: 'users',
    name: 'User Management',
    icon: 'users',
    permissions: { view: true, edit: false },
    children: [
      { id: 'userlist', name: 'User List', permissions: { view: true, edit: false } },
      { id: 'roles', name: 'Roles & Permissions', permissions: { view: false, edit: false } },
      { id: 'activity', name: 'Activity Logs', permissions: { view: true, edit: false } },
    ],
  },
  {
    id: 'payments',
    name: 'Payments',
    icon: 'payments',
    permissions: { view: true, edit: false },
    children: [
      { id: 'transactions', name: 'Transactions', permissions: { view: true, edit: false } },
      { id: 'invoices', name: 'Invoices', permissions: { view: true, edit: false } },
      { id: 'refunds', name: 'Refunds', permissions: { view: false, edit: false } },
    ],
  },
  {
    id: 'reports',
    name: 'Reports & Analytics',
    icon: 'reports',
    permissions: { view: true, edit: false },
    children: [
      { id: 'sales', name: 'Sales Report', permissions: { view: true, edit: false } },
      { id: 'inventory', name: 'Inventory Report', permissions: { view: true, edit: false } },
      { id: 'custom', name: 'Custom Reports', permissions: { view: false, edit: false } },
    ],
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: 'settings',
    permissions: { view: true, edit: false },
    children: [
      { id: 'general', name: 'General Settings', permissions: { view: true, edit: false } },
      { id: 'security', name: 'Security', permissions: { view: false, edit: false } },
      { id: 'notifications', name: 'Notifications', permissions: { view: true, edit: false } },
      { id: 'api', name: 'API Keys', permissions: { view: false, edit: false } },
    ],
  },
];

// API Service
const apiService = {
  savePermissions: async (data: Page[]): Promise<{ success: boolean; message: string }> => {
    return new Promise(resolve => {
      setTimeout(() => {
        console.log('Saving permissions to backend:', data);
        resolve({
          success: true,
          message: 'Permissions saved successfully!',
        });
      }, 1500);
    });
  },
};

// Main Component
const AccessControlUI: React.FC = () => {
  const [pages, setPages] = useState<Page[]>(MOCK_DATA);
  const [originalPages] = useState<Page[]>(JSON.parse(JSON.stringify(MOCK_DATA)));
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ dashboard: true });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(pages) !== JSON.stringify(originalPages);
  }, [pages, originalPages]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleParentChange = useCallback((pageId: string, key: keyof Permission) => {
    setPages(prev =>
      prev.map(p => {
        if (p.id !== pageId) return p;

        const newValue = !p.permissions[key];

        // If disabling View, also disable Edit
        const updatedPermissions =
          key === 'view' && !newValue ? { view: false, edit: false } : { ...p.permissions, [key]: newValue };

        return {
          ...p,
          permissions: updatedPermissions,
          children: p.children.map(c => {
            // Apply same logic to children
            const childPermissions =
              key === 'view' && !newValue ? { view: false, edit: false } : { ...c.permissions, [key]: newValue };

            return {
              ...c,
              permissions: childPermissions,
            };
          }),
        };
      }),
    );
  }, []);

  const handleChildChange = useCallback((pageId: string, childId: string, key: keyof Permission) => {
    setPages(prev =>
      prev.map(p => {
        if (p.id !== pageId) return p;

        const updatedChildren = p.children.map(c => {
          if (c.id !== childId) return c;

          const newValue = !c.permissions[key];

          // If enabling Edit, also enable View
          // If disabling View, also disable Edit
          if (key === 'edit' && newValue) {
            return {
              ...c,
              permissions: { view: true, edit: true },
            };
          } else if (key === 'view' && !newValue) {
            return {
              ...c,
              permissions: { view: false, edit: false },
            };
          }

          return {
            ...c,
            permissions: { ...c.permissions, [key]: newValue },
          };
        });

        // Recalculate parent state based on children
        const allTrue = updatedChildren.every(c => c.permissions[key]);
        const someTrue = updatedChildren.some(c => c.permissions[key]);

        // Parent should be checked if all children are checked
        // Parent should be unchecked if no children are checked
        // Parent remains in current state if some children are checked (indeterminate)
        let parentValue = p.permissions[key];
        if (allTrue) {
          parentValue = true;
        } else if (!someTrue) {
          parentValue = false;
        }

        // If View is being turned off for all children, turn off Edit too
        const updatedParentPermissions =
          key === 'view' && !someTrue ? { view: false, edit: false } : { ...p.permissions, [key]: parentValue };

        return {
          ...p,
          children: updatedChildren,
          permissions: updatedParentPermissions,
        };
      }),
    );
  }, []);

  const isIndeterminate = useCallback((page: Page, key: keyof Permission): boolean => {
    const values = page.children.map(c => c.permissions[key]);
    const someTrue = values.some(Boolean);
    const allTrue = values.every(Boolean);
    return someTrue && !allTrue;
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const result = await apiService.savePermissions(pages);
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error',
      });

      // Update original pages after successful save
      if (result.success) {
        // This would normally be done after API confirmation
        // originalPages would be updated in the actual implementation
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to save permissions. Please try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [pages]);

  const expandAll = useCallback(() => {
    const allExpanded: Record<string, boolean> = {};
    pages.forEach(page => {
      allExpanded[page.id] = true;
    });
    setExpanded(allExpanded);
  }, [pages]);

  const collapseAll = useCallback(() => {
    setExpanded({});
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const resetChanges = useCallback(() => {
    setPages(JSON.parse(JSON.stringify(originalPages)));
    setSnackbar({
      open: true,
      message: 'Changes have been reset',
      severity: 'info' as 'success',
    });
  }, [originalPages]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        transition: 'all 0.3s ease',
      }}
    >
      <Box p={4}>
        {/* Header */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Access Control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage user permissions and access levels
            </Typography>
            {hasChanges && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                <Typography variant="caption" color="warning.main" fontWeight={500}>
                  You have unsaved changes
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Tooltip title="Expand all modules">
              <Button
                variant="outlined"
                onClick={expandAll}
                sx={{
                  color: 'text.primary',
                  borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                Expand All
              </Button>
            </Tooltip>
            <Tooltip title="Collapse all modules">
              <Button
                variant="outlined"
                onClick={collapseAll}
                sx={{
                  color: 'text.primary',
                  borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                Collapse All
              </Button>
            </Tooltip>
            {hasChanges && (
              <Tooltip title="Reset to original state">
                <Button
                  variant="outlined"
                  onClick={resetChanges}
                  startIcon={<CancelOutlined />}
                  sx={{
                    color: 'secondary.main',
                    borderColor: theme => alpha(theme.palette.secondary.main, 0.3),
                    '&:hover': {
                      borderColor: 'secondary.main',
                      bgcolor: theme => alpha(theme.palette.secondary.main, 0.04),
                    },
                  }}
                >
                  Reset
                </Button>
              </Tooltip>
            )}
            <Tooltip title={hasChanges ? 'Save all changes' : 'No changes to save'}>
              <span>
                <SaveButton
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </SaveButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Permissions Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, 0.1),
          }}
        >
          {/* Header Row */}
          <Row
            sx={{
              borderBottom: theme => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Info>
              <Typography fontWeight="bold">Module / Feature</Typography>
            </Info>
            <Col>
              <Tooltip title="View permission allows read-only access">
                <Typography fontWeight="bold" color="primary.main">
                  View Access
                </Typography>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Edit permission requires View access">
                <Typography fontWeight="bold" color="primary.main">
                  Edit Access
                </Typography>
              </Tooltip>
            </Col>
          </Row>

          {/* Rows */}
          {pages.map(page => (
            <Box key={page.id}>
              <Row>
                <Info>
                  <Tooltip title={expanded[page.id] ? 'Collapse' : 'Expand'}>
                    <IconButton onClick={() => toggleExpand(page.id)} size="small">
                      {expanded[page.id] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Tooltip>
                  <AvatarBox>{getIcon(page.icon)}</AvatarBox>
                  <Typography fontWeight="medium">{page.name}</Typography>
                </Info>

                <Col>
                  <Tooltip
                    title={
                      isIndeterminate(page, 'view')
                        ? 'Some features have view access'
                        : page.permissions.view
                        ? 'All features have view access'
                        : 'No view access'
                    }
                  >
                    <span>
                      <StyledSwitch
                        checked={page.permissions.view}
                        indeterminate={isIndeterminate(page, 'view')}
                        onChange={() => handleParentChange(page.id, 'view')}
                      />
                    </span>
                  </Tooltip>
                </Col>

                <Col>
                  <Tooltip
                    title={
                      isIndeterminate(page, 'edit')
                        ? 'Some features have edit access'
                        : page.permissions.edit
                        ? 'All features have edit access'
                        : 'No edit access'
                    }
                  >
                    <span>
                      <StyledSwitch
                        checked={page.permissions.edit}
                        indeterminate={isIndeterminate(page, 'edit')}
                        onChange={() => handleParentChange(page.id, 'edit')}
                      />
                    </span>
                  </Tooltip>
                </Col>
              </Row>

              <Collapse in={expanded[page.id]}>
                {page.children.map(child => (
                  <Row
                    key={child.id}
                    sx={{
                      pl: 8,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Info>
                      <Typography variant="body2" color="text.secondary">
                        {child.name}
                      </Typography>
                    </Info>

                    <Col>
                      <Tooltip title={child.permissions.view ? 'Has view access' : 'No view access'}>
                        <span>
                          <StyledSwitch
                            checked={child.permissions.view}
                            onChange={() => handleChildChange(page.id, child.id, 'view')}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </Col>

                    <Col>
                      <Tooltip
                        title={
                          !child.permissions.view && !child.permissions.edit
                            ? 'Enable View access first'
                            : child.permissions.edit
                            ? 'Has edit access'
                            : 'No edit access'
                        }
                      >
                        <span>
                          <StyledSwitch
                            checked={child.permissions.edit}
                            onChange={() => handleChildChange(page.id, child.id, 'edit')}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </Col>
                  </Row>
                ))}
              </Collapse>
            </Box>
          ))}
        </Paper>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            variant="filled"
            sx={{
              width: '100%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default AccessControlUI;
